import { getFirestore, doc, updateDoc, Timestamp, getDoc } from 'firebase/firestore';

/**
 * Database utility functions for managing the file and folder system
 * This handles all Firestore operations for files and folders
 */

/**
 * Get or create the protected "User Uploads" folder for a user
 * @param {string} userId - The user's ID
 * @returns {Promise<string>} - The folder ID of the User Uploads folder
 */
export const getOrCreateUserUploadsFolder = async (userId) => {
    try {
        const db = getFirestore();
        const userRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userRef);
        
        if (!userDoc.exists()) {
            throw new Error('User document not found');
        }
        
        const userData = userDoc.data();
        const existingFolders = userData.folders || {};
        
        // Check if User Uploads folder already exists
        const existingProtectedFolder = Object.entries(existingFolders).find(([id, folder]) => 
            folder.protected === true && folder.name === 'User Uploads'
        );
        
        if (existingProtectedFolder) {
            return existingProtectedFolder[0]; // Return existing folder ID
        }
        
        // Create new User Uploads folder
        const userUploadsFolderId = `folder_${Date.now()}_protected_${userId}`;
        const protectedFolderData = {
            name: 'User Uploads',
            color: '#2ecc71',
            parentId: null,
            order: 0, // Put it first
            protected: true, // Prevent deletion
            backgroundImg: '',
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now()
        };
        
        const updatedFolders = { ...existingFolders, [userUploadsFolderId]: protectedFolderData };
        
        await updateDoc(userRef, { folders: updatedFolders });
        
        console.log(`✅ Created User Uploads folder for user ${userId}`);
        return userUploadsFolderId;
        
    } catch (error) {
        console.error('Error getting/creating User Uploads folder:', error);
        throw error;
    }
};

/**
 * Get or create the "Discussion Board Media" subfolder within User Uploads
 * @param {string} userId - The user's ID
 * @returns {Promise<string>} - The folder ID of the Discussion Board Media subfolder
 */
export const getOrCreateDiscussionBoardMediaSubfolder = async (userId) => {
    try {
        const db = getFirestore();
        const userRef = doc(db, 'users', userId);
        
        // First, get or create the User Uploads folder
        const userUploadsFolderId = await getOrCreateUserUploadsFolder(userId);
        
        console.log('User Uploads folder ID:', userUploadsFolderId);
        // Get fresh user data after potentially creating User Uploads folder
        const userDoc = await getDoc(userRef);
        console.log('User document:', userDoc.data());
        if (!userDoc.exists()) {
            throw new Error('User document not found');
        }
        console.log('User document exists');
        const userData = userDoc.data();
        const existingFolders = userData.folders || {};
        console.log('Existing folders:', existingFolders);
        // Check if Discussion Board Media subfolder already exists within User Uploads
        const existingDiscussionSubfolder = Object.entries(existingFolders).find(([id, folder]) => 
            folder.name === 'Discussion Board Media' && folder.parentId === userUploadsFolderId
        );
        
        if (existingDiscussionSubfolder) {
            console.log(`✅ Found existing Discussion Board Media subfolder for user ${userId}`);
            return existingDiscussionSubfolder[0]; // Return existing subfolder ID
        }
        console.log('No existing Discussion Board Media subfolder found');
        // Create new Discussion Board Media subfolder within User Uploads
        const discussionSubfolderId = `folder_${Date.now()}_discussion_sub_${userId}`;
        const discussionSubfolderData = {
            name: 'Discussion Board Media',
            color: '#3498db',
            parentId: userUploadsFolderId, // Parent is User Uploads folder
            order: 0, // First subfolder in User Uploads
            protected: false, // Allow deletion
            backgroundImg: '',
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now()
        };
        console.log('Discussion subfolder data:', discussionSubfolderData);
        const updatedFolders = { ...existingFolders, [discussionSubfolderId]: discussionSubfolderData };
        console.log('Updated folders:', updatedFolders);
        await updateDoc(userRef, { folders: updatedFolders });
        
        console.log(`✅ Created Discussion Board Media subfolder within User Uploads for user ${userId}`);
        return discussionSubfolderId;
        
    } catch (error) {
        console.error('Error getting/creating Discussion Board Media subfolder:', error);
        throw error;
    }
};

/**
 * Add a file reference to the user's folder system
 * @param {string} userId - The user's ID
 * @param {string} fileName - Display name for the file
 * @param {string} fileUrl - Download URL of the file
 * @param {string} uploadSource - Source of upload ('venue_report', 'discussion', etc.)
 * @param {Object} metadata - Additional metadata for the file
 * @returns {Promise<string>} - The file ID that was created
 */
export const addFileToUserUploads = async (userId, fileName, fileUrl, uploadSource, metadata = {}) => {
    try {
        const db = getFirestore();
        const userRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userRef);
        
        if (!userDoc.exists()) {
            throw new Error('User document not found');
        }
        
        const userData = userDoc.data();
        const existingFiles = userData.files || {};
        
        // Use custom target folder if provided, otherwise get or create User Uploads folder
        const userUploadsFolderId = metadata.targetFolderId || await getOrCreateUserUploadsFolder(userId);
        // Create file entry
        const fileId = `file_upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const fileData = {
            name: fileName,
            id: fileId,
            parentId: userUploadsFolderId,
            itemType: 'file',
            data: {
                fileType: metadata.mediaType || 'image', // Default to image if not specified
                url: fileUrl,
                manualUpload: false
            },
            order: Object.values(existingFiles).filter(f => f.parentId === userUploadsFolderId).length,
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now(),
            uploadSource: uploadSource,
            storagePath: fileUrl, // Use URL as storage path for external uploads
            ...metadata // Spread any additional metadata
        };
        
        const updatedFiles = { ...existingFiles, [fileId]: fileData };
        
        await updateDoc(userRef, { files: updatedFiles });
        
        return fileId;
        
    } catch (error) {
        console.error('Error adding file to User Uploads:', error);
        // Don't throw error - we don't want to break the original upload process
        // Just log it and continue
        return null;
    }
};

/**
 * Add multiple files to User Uploads folder
 * @param {string} userId - The user's ID
 * @param {Array} files - Array of file objects with {name, url, metadata}
 * @param {string} uploadSource - Source of upload
 * @returns {Promise<Array>} - Array of created file IDs
 */
export const addMultipleFilesToUserUploads = async (userId, files, uploadSource) => {
    try {
        const fileIds = [];
        
        for (const file of files) {
            const fileId = await addFileToUserUploads(
                userId, 
                file.name, 
                file.url, 
                uploadSource, 
                file.metadata || {}
            );
            if (fileId) {
                fileIds.push(fileId);
            }
        }
        
        console.log(`✅ Added ${fileIds.length} files to User Uploads folder for user ${userId}`);
        return fileIds;
        
    } catch (error) {
        console.error('Error adding multiple files to User Uploads:', error);
        return [];
    }
};

/**
 * Add a file to the root folder (no specific folder)
 * @param {string} userId - The user's ID
 * @param {string} fileName - Display name for the file
 * @param {string} fileUrl - Download URL of the file
 * @param {string} uploadSource - Source of upload ('venue_report', 'discussion', etc.)
 * @param {Object} metadata - Additional metadata for the file
 * @returns {Promise<string>} - The file ID that was created
 */
export const addFileToRootFolder = async (userId, fileName, fileUrl, uploadSource, metadata = {}) => {
    try {
        const db = getFirestore();
        const userRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userRef);
        
        if (!userDoc.exists()) {
            throw new Error('User document not found');
        }
        
        const userData = userDoc.data();
        const existingFiles = userData.files || {};
        
        // Create file entry for root folder (no folderId)
        const fileId = `file_root_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const fileData = {
            folderId: null, // Root folder - no specific folder
            name: fileName,
            url: fileUrl,
            storagePath: fileUrl, // Use URL as storage path for external uploads
            order: Object.values(existingFiles).filter(f => f.folderId === null).length,
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now(),
            protected: false, // Root files are not protected by default
            uploadSource: uploadSource,
            ...metadata // Spread any additional metadata
        };
        
        const updatedFiles = { ...existingFiles, [fileId]: fileData };
        
        await updateDoc(userRef, { files: updatedFiles });
        
        console.log(`✅ Added file "${fileName}" to root folder for user ${userId}`);
        return fileId;
        
    } catch (error) {
        console.error('Error adding file to root folder:', error);
        // Don't throw error - we don't want to break the original upload process
        // Just log it and continue
        return null;
    }
};

/**
 * Remove a file from the User Uploads folder
 * @param {string} userId - The user's ID
 * @param {string} fileUrl - The URL of the file to remove
 * @returns {Promise<boolean>} - Whether the file was successfully removed
 */
export const removeFileFromUserUploads = async (userId, fileUrl) => {
    try {
        const db = getFirestore();
        const userRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userRef);
        
        if (!userDoc.exists()) {
            console.log('User document not found');
            return false;
        }
        
        const userData = userDoc.data();
        const existingFiles = userData.files || {};
        
        // Find the file by URL
        const fileToRemove = Object.entries(existingFiles).find(([id, file]) => 
            file.url === fileUrl || file.storagePath === fileUrl
        );
        
        if (!fileToRemove) {
            console.log('File not found in User Uploads folder');
            return false;
        }
        
        const [fileId, fileData] = fileToRemove;
        
        // Remove the file from the files object
        const updatedFiles = { ...existingFiles };
        delete updatedFiles[fileId];
        
        // Update the user document
        await updateDoc(userRef, { files: updatedFiles });
        
        console.log(`✅ Removed file "${fileData.name}" from User Uploads folder for user ${userId}`);
        return true;
        
    } catch (error) {
        console.error('Error removing file from User Uploads:', error);
        return false;
    }
};

/**
 * Remove a file from the root folder
 * @param {string} userId - The user's ID
 * @param {string} fileUrl - The URL of the file to remove
 * @returns {Promise<boolean>} - Whether the file was successfully removed
 */
export const removeFileFromRootFolder = async (userId, fileUrl) => {
    try {
        const db = getFirestore();
        const userRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userRef);
        
        if (!userDoc.exists()) {
            console.log('User document not found');
            return false;
        }
        
        const userData = userDoc.data();
        const existingFiles = userData.files || {};
        
        // Find the file by URL in root folder (folderId === null)
        const fileToRemove = Object.entries(existingFiles).find(([id, file]) => 
            (file.url === fileUrl || file.storagePath === fileUrl) && file.folderId === null
        );
        
        if (!fileToRemove) {
            console.log('File not found in root folder');
            return false;
        }
        
        const [fileId, fileData] = fileToRemove;
        
        // Remove the file from the files object
        const updatedFiles = { ...existingFiles };
        delete updatedFiles[fileId];
        
        // Update the user document
        await updateDoc(userRef, { files: updatedFiles });
        
        console.log(`✅ Removed file "${fileData.name}" from root folder for user ${userId}`);
        return true;
        
    } catch (error) {
        console.error('Error removing file from root folder:', error);
        return false;
    }
};

/**
 * Get files from the root folder
 * @param {string} userId - The user's ID
 * @returns {Promise<Array>} - Array of files in the root folder
 */
export const getRootFolderFiles = async (userId) => {
    try {
        const db = getFirestore();
        const userRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userRef);
        
        if (!userDoc.exists()) {
            return [];
        }
        
        const userData = userDoc.data();
        const files = userData.files || {};
        
        // Get files from root folder (folderId === null)
        const rootFiles = Object.entries(files)
            .filter(([id, file]) => file.folderId === null)
            .map(([id, file]) => ({ id, ...file }))
            .sort((a, b) => a.order - b.order);
        
        return rootFiles;
        
    } catch (error) {
        console.error('Error getting root folder files:', error);
        return [];
    }
};

/**
 * Get user uploads statistics
 * @param {string} userId - The user's ID
 * @returns {Promise<Object>} - Statistics about user uploads
 */
export const getUserUploadsStats = async (userId) => {
    try {
        const db = getFirestore();
        const userRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userRef);
        
        if (!userDoc.exists()) {
            return { hasUserUploadsFolder: false, totalFiles: 0, filesBySource: {} };
        }
        
        const userData = userDoc.data();
        const folders = userData.folders || {};
        const files = userData.files || {};
        
        // Find User Uploads folder
        const userUploadsFolder = Object.entries(folders).find(([id, folder]) => 
            folder.protected === true && folder.name === 'User Uploads'
        );
        
        if (!userUploadsFolder) {
            return { hasUserUploadsFolder: false, totalFiles: 0, filesBySource: {} };
        }
        
        const userUploadsFolderId = userUploadsFolder[0];
        
        // Count files in User Uploads folder
        const userUploadFiles = Object.values(files).filter(file => file.folderId === userUploadsFolderId);
        const filesBySource = {};
        
        userUploadFiles.forEach(file => {
            const source = file.uploadSource || file.migratedFrom || 'unknown';
            filesBySource[source] = (filesBySource[source] || 0) + 1;
        });
        
        return {
            hasUserUploadsFolder: true,
            userUploadsFolderId,
            totalFiles: userUploadFiles.length,
            filesBySource
        };
        
    } catch (error) {
        console.error('Error getting user uploads stats:', error);
        return { hasUserUploadsFolder: false, totalFiles: 0, filesBySource: {} };
    }
};
